import { useCallback, useEffect, useMemo, useState, type FormEvent, type JSX, type ReactNode } from 'react'
import { Why } from './ui'
import {
  api,
  codeWords,
  countdownWords,
  durationWords,
  localInputStamp,
  noClockCatalogue,
  shortDate,
  stampWithZone,
  tierLabel,
  windowWords,
  type ConcentrationEntry,
  type ExposedElement,
  type ExposedSubprocessor,
  type Exposure,
  type DutyNotice,
  type ExposureClock,
  type FactSource,
  type Incident,
} from '../lib/api'
import { BandChip } from './BandChip'
import { DataTable, ErrorBox, Loading, type Column } from './DataTable'
import { Disclosure, Status, Toggletip, type Tone } from './kit'
import { ReasonTip } from './vendor/ReasonTip'

// The incident answer: what of ours is exposed, roughly how many people, who
// has to be told and by when, and who else sits behind the same supplier.
//
// THE ORDER IS THE ORDER THE QUESTIONS ARE ASKED IN
//
// Somebody opens this at two in the morning with a vendor on the phone. The
// four sections below are the four things they are about to be asked, in the
// sequence they get asked, and nothing else is allowed above them. Dependency,
// standing findings and the provenance footer are all real and all sit
// underneath, because none of them is what the first call is about.
//
// EVERY FIGURE SAYS IT RESTS ON DECLARATION
//
// docs/DECISIONS.md settles that this is built from what the tenant has
// already recorded, and that observation replaces declaration field by field
// later. So each block carries the source of its facts, and the subject count
// is printed as the band that was recorded. There is no arithmetic anywhere in
// this file that turns a band into a number, and there must never be: a band
// shown as a figure would be the console inventing precision about how many
// people have to be told.
//
// THE DEADLINE IS THE API'S, THE COUNTDOWN IS THIS BROWSER'S
//
// dueAt arrives computed from the incident's discovery time and the window the
// statutory clock catalogue holds. The time remaining is arithmetic against
// this machine's clock, refreshed on a timer, because an answer left open for
// an hour must not still be claiming four hours are left. A browser whose
// clock is wrong moves the countdown and never the deadline, which is why the
// deadline is always printed as a date and a time and not only as a duration.

// How often the countdowns are recomputed. Half a minute is short enough that
// nothing on screen is meaningfully stale and long enough that the page is not
// repainting under somebody who is reading it.
export const TICK_MS = 30000

export function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), intervalMs)
    return () => window.clearInterval(t)
  }, [intervalMs])
  return now
}

const SOURCE_CHIP: Record<FactSource, { cls: string; label: string; title: string }> = {
  DECLARED: {
    cls: 'prov prov-internal',
    label: 'Declared',
    title: 'Recorded by this organisation on the vendor record. Nothing here was observed at the vendor.',
  },
  OBSERVED: {
    cls: 'prov prov-doc',
    label: 'Observed',
    title: 'Filled in by a platform product that watched it, rather than by somebody recording it.',
  },
}

export function SourceChip({ source }: { source: FactSource | undefined }): JSX.Element {
  const spec = SOURCE_CHIP[source ?? 'DECLARED'] ?? SOURCE_CHIP.DECLARED
  return (
    <span className={spec.cls} title={spec.title}>
      {spec.label}
    </span>
  )
}

// The two answers to "by when", and they are different questions.
//
// A duty owed without delay is owed now and never runs down, so it cannot be
// found by looking for the earliest deadline and it must not be hidden behind
// one either. The register and the header carry both: the duty owed now, said
// in words, and the nearest thing with a clock on it, counted down. Reporting
// only the first would hide the six hour window; reporting only the second
// would let a duty owed now sit unread behind a countdown that looks calmer.
export function immediateClock(clocks: ExposureClock[]): ExposureClock | null {
  return clocks.find((c) => c.applicable && c.owedWithoutDelay && !c.told) ?? null
}

export function nearestClock(clocks: ExposureClock[]): ExposureClock | null {
  // A duty somebody recorded as told is met, so it is never the next thing.
  const timed = clocks.filter((c) => c.applicable && !c.owedWithoutDelay && !c.told)
  if (timed.length === 0) return null
  return timed.reduce((a, b) => (Date.parse(b.dueAt) < Date.parse(a.dueAt) ? b : a))
}

// Applicable first, and within those the duties owed now, then the nearest
// deadline. The ones that do not apply keep catalogue order underneath,
// because that block is read to check that a duty was considered rather than
// scanned for the next thing to do.
function inUrgencyOrder(clocks: ExposureClock[]): ExposureClock[] {
  return [...clocks].sort((a, b) => {
    if (a.applicable !== b.applicable) return a.applicable ? -1 : 1
    if (!a.applicable) return 0
    if (Boolean(a.owedWithoutDelay) !== Boolean(b.owedWithoutDelay)) return a.owedWithoutDelay ? -1 : 1
    return Date.parse(a.dueAt) - Date.parse(b.dueAt)
  })
}

// The catalogue writes "This organisation" and "The Reserve Bank of India" as
// if each began a sentence, because in the printed table each one does. Here
// they sit inside one, and a capital in the middle of a line is the sort of
// thing a reader trips on at three in the morning. Only the handful of leading
// function words are lowered, so a party whose legal name genuinely starts the
// phrase keeps its capital.
const OPENERS = new Set(['This', 'The', 'Each', 'Every', 'A', 'An', 'Our', 'Both'])

function midSentence(phrase: string): string {
  const trimmed = phrase.replace(/\.$/, '')
  const first = trimmed.split(' ')[0] ?? ''
  return OPENERS.has(first) ? first.toLowerCase() + trimmed.slice(first.length) : trimmed
}

// The clock's state as a kit label. A duty that does not apply is settled and
// goes quiet; one owed without delay is owed now, so it is the warning; one
// past its deadline is the critical one.
function clockState(c: ExposureClock, now: number): { cls: string; tone: Tone; words: string } {
  if (!c.applicable) {
    return { cls: 'act-item act-off', tone: 'plain', words: 'Does not apply' }
  }
  if (c.told) {
    return { cls: 'act-item act-met', tone: 'ok', words: 'Told' }
  }
  if (c.owedWithoutDelay) {
    return { cls: 'act-item', tone: 'warn', words: 'Owed without delay' }
  }
  const ms = Date.parse(c.dueAt) - now
  if (Number.isNaN(ms)) return { cls: 'act-item', tone: 'neutral', words: 'No deadline' }
  if (ms < 0) {
    return { cls: 'act-item act-unmet', tone: 'crit', words: 'Overdue by ' + durationWords(ms) }
  }
  return { cls: 'act-item', tone: 'info', words: countdownWords(ms) }
}

// Who a told record came through, in words.
function toldVia(n: DutyNotice): string {
  switch (n.via) {
    case 'FOCTTA_CONSENT':
      return 'recorded by FOCTTA Consent'
    case 'REGISTRY_KEY':
      return 'recorded by another breach tool'
  }
  return 'recorded here by ' + n.recordedBy
}

type TellInput = { toldAt: string; toldBy?: string; reference?: string }

// What a duty says under its name, line by line: who was told and when, or
// from when it is owed, or by when, or why it does not apply. The card says
// these as paragraphs; the vendor record's table (ExposureAnswer, folded)
// says the same lines in its When column.
function clockLines(clock: ExposureClock, discoveredAt: string, now: number): { key: string; line: ReactNode }[] {
  const remaining = Date.parse(clock.dueAt) - now
  const out: { key: string; line: ReactNode }[] = []
  if (clock.told) {
    out.push({
      key: 'told',
      line: (
        <>
          Told {stampWithZone(clock.told.at)} by {clock.told.by}
          {clock.told.reference ? ', reference ' + clock.told.reference : ''}
          <Toggletip label="Where this record came from">
            {'This duty is met, so nothing chases it. It was ' +
              toldVia(clock.told) +
              ' on ' +
              stampWithZone(clock.told.recordedAt) +
              (clock.told.breachReference ? ', from breach record ' + clock.told.breachReference : '') +
              '.'}
          </Toggletip>
        </>
      ),
    })
  }
  if (clock.applicable && !clock.told && clock.owedWithoutDelay) {
    out.push({
      key: 'owed',
      line: (
        <>
          Owed from discovery, {stampWithZone(discoveredAt)}, {durationWords(now - Date.parse(discoveredAt))} ago
          <Toggletip label="Why it is not shown as overdue">
            A duty owed without delay has no hour count to run past, and a countdown against it would say something the instrument does not.
          </Toggletip>
        </>
      ),
    })
  }
  if (clock.applicable && !clock.told && !clock.owedWithoutDelay) {
    out.push({
      key: 'due',
      line: (
        <>
          Due by <strong className="mono">{stampWithZone(clock.dueAt)}</strong>
          {remaining < 0 ? <Status tone="crit">Report late</Status> : <span>{countdownWords(remaining)}</span>}
          <Toggletip label="About this window">{windowWords(clock.window) + ' from discovery'}</Toggletip>
        </>
      ),
    })
  }
  // The API's own sentence, which already opens by saying the duty is not
  // engaged and then names the fact that decided it. The heading above and
  // the pill beside the name say the rest, and a lead-in here would be the
  // screen saying the same thing three times.
  if (!clock.applicable) {
    out.push({
      key: 'off',
      line: (
        <>
          {clock.notApplicable && clock.notApplicable.length <= 80
            ? clock.notApplicable
            : clock.notApplicable
              ? 'Not engaged by these facts'
              : 'The catalogue gave no reason, which is worth raising'}
          {clock.notApplicable && clock.notApplicable.length > 80 && <ReasonTip label="Why it does not apply" text={clock.notApplicable} />}
        </>
      ),
    })
  }
  return out
}

function ClockCard({
  clock,
  discoveredAt,
  now,
  onTell,
}: {
  clock: ExposureClock
  discoveredAt: string
  now: number
  // Set on an incident, for somebody who may record it. Absent on a what-if
  // answer, which has nobody to have told.
  onTell?: (code: string, input: TellInput) => Promise<void>
}) {
  const state = clockState(clock, now)
  const facts = clock.because ?? []

  return (
    <li className={state.cls}>
      <div className="act-head">
        <strong>{clock.name}</strong>
        <ReasonTip label={'Why ' + clock.name + ' is in the catalogue'} text={clock.why} />
        <Status tone={state.tone}>{state.words}</Status>
      </div>

      {/* The duty in one line, then the deadline, then why it is on. A reader
          who stops after the first two lines has what they came for. Who is
          told is what a reader scans for; which capacity this organisation
          owes it in is the same on most cards, so it sits behind the (i). */}
      <p className="act-said vd-inline">
        To {midSentence(clock.owedTo)}
        <Toggletip label="Who owes this">{'Owed by ' + midSentence(clock.owedBy) + '.'}</Toggletip>
      </p>

      {clockLines(clock, discoveredAt, now).map((l) => (
        <p key={l.key} className="act-said vd-inline">
          {l.line}
        </p>
      ))}

      {/* The instrument and what triggers it, folded.
          
          These are legal citations: the section of the CERT-In directions or
          the RBI master direction that imposes this duty. A compliance
          officer needs them and needs them exact, so they are not shortened.
          They are also two long paragraphs per duty, sixteen duties to a
          vendor, and the card above already says what is owed, to whom and by
          when, which is what a reader came for. The evidence is one press
          away rather than in the way. */}
      <Why label="Under which instrument">
        <p className="act-detail">{clock.instrument}</p>
        <p className="act-detail">{clock.trigger}</p>
      </Why>

      {/* The fact and the answer, not the answer alone. A predicate can turn
          on a fact being false as easily as true, and a line reading "on
          because yes" tells a reader nothing about what was yes. */}
      {clock.applicable && facts.length > 0 && (
        <p className="act-detail">
          On because{' '}
          {facts.map((f, i) => (
            <span key={f.fact}>
              {i > 0 ? '; ' : ''}
              <strong>{f.fact}</strong>: {f.value}
            </span>
          ))}
          . <SourceChip source={facts[0]?.source} />
        </p>
      )}

      {clock.applicable && !clock.told && onTell && <TellForm code={clock.code} name={clock.name} onTell={onTell} />}
    </li>
  )
}

// "Record that we told them". Who told them is left empty to mean the person
// recording it; it is often somebody else, and then they type who.
function TellForm({
  code,
  name,
  onTell,
}: {
  code: string
  name: string
  onTell: (code: string, input: TellInput) => Promise<void>
}) {
  const [open, setOpen] = useState(false)
  const [when, setWhen] = useState(() => localInputStamp(new Date()))
  const [by, setBy] = useState('')
  const [reference, setReference] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<unknown>(null)

  if (!open) {
    return (
      <div className="act-actions">
        <button type="button" className="button ghost" onClick={() => setOpen(true)}>
          Record that we told them
        </button>
      </div>
    )
  }

  async function save(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setProblem(null)
    try {
      await onTell(code, {
        toldAt: new Date(when).toISOString(),
        toldBy: by.trim() || undefined,
        reference: reference.trim() || undefined,
      })
      setOpen(false)
    } catch (err) {
      setProblem(err)
    } finally {
      setBusy(false)
    }
  }

  const id = 'tell-' + code.toLowerCase()
  return (
    <form className="wizard-form act-form" onSubmit={(e) => void save(e)} aria-label={'Record that ' + name + ' was told'}>
      <label htmlFor={id + '-when'}>
        When they were told
        <input
          id={id + '-when'}
          type="datetime-local"
          value={when}
          max={localInputStamp(new Date())}
          onChange={(e) => setWhen(e.target.value)}
          required
        />
      </label>
      <label htmlFor={id + '-by'}>
        Who told them
        <input id={id + '-by'} value={by} onChange={(e) => setBy(e.target.value)} placeholder="You, if left empty" />
      </label>
      <label htmlFor={id + '-ref'}>
        Their acknowledgement or reference
        <input id={id + '-ref'} value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Optional" />
      </label>
      {problem != null && <ErrorBox error={problem} what="that record" verb="saved" />}
      <div className="act-actions">
        <button type="submit" className="button primary" data-action="tprm.incident,incidents.a02" disabled={busy}>
          {busy ? 'Recording' : 'Record it'}
        </button>
        <button type="button" className="button ghost" onClick={() => setOpen(false)} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  )
}

// The four questions, in order, and everything else underneath them.
export function ExposureAnswer({
  exposure,
  onRecompute,
  busy,
  onTell,
  folded = false,
}: {
  exposure: Exposure
  onRecompute?: () => void
  busy?: boolean
  // On an incident, for somebody who may record that a duty was met.
  onTell?: (code: string, input: TellInput) => Promise<void>
  // The vendor record's Exposure part (release 3.3, lane 2 leftovers): the
  // head and its four answers on one line, then each question folded with
  // what it holds said on its line, the duties one table rather than a card
  // each and the long lists opening on their first rows. As sections it was
  // two to seven windows long on a vendor with standing findings. An
  // incident, where duties are recorded as told, keeps the sections.
  folded?: boolean
}): JSX.Element {
  const now = useNow(TICK_MS)
  const data = exposure.data ?? null
  const placement = exposure.placement ?? null
  const clocks = useMemo(() => inUrgencyOrder(exposure.clocks), [exposure.clocks])
  const live = clocks.filter((c) => c.applicable)
  const considered = clocks.filter((c) => !c.applicable)
  const next = nearestClock(exposure.clocks)
  // Every duty the instrument owes at once, not the first of them. Two can be
  // engaged together, and the strip is the line the rest of this screen says
  // nothing is allowed to sit above.
  const owedNowAll = exposure.clocks.filter((c) => c.applicable && c.owedWithoutDelay && !c.told)
  // A concentration row is one party reached through one other vendor, so a
  // vendor sharing two named parties appears twice. The question asked is how
  // many other vendors, which is the distinct count.
  const otherVendors = new Set(exposure.concentration.map((c) => c.vendorId)).size
  const overdueCount = live.filter((c) => !c.told && !c.owedWithoutDelay && Date.parse(c.dueAt) - now < 0).length
  const toldCount = live.filter((c) => c.told).length

  // The elements only, as T12 of the review of 21 September did for "What this
  // vendor processes": a recorded note is behind a hover beside its element
  // rather than a column of its own, so the table reads as what is exposed.
  // Every note is still one hover away, because at two in the morning the
  // note may be the thing that says what an element actually holds.
  const elementColumns: Column<ExposedElement>[] = [
    {
      key: 'element',
      header: 'Data element',
      sortValue: (r) => r.elementName || r.elementCode,
      render: (r) => (
        <>
          <span className="cell-primary vd-inline">
            {r.elementName || r.elementCode}
            {r.note && <ReasonTip hover label={'Note on ' + (r.elementName || r.elementCode).toLowerCase()} text={r.note} />}
          </span>
          <span className="cell-sub mono">{r.elementCode}</span>
        </>
      ),
    },
    {
      key: 'classification',
      header: 'Classification',
      sortValue: (r) => r.classification,
      render: (r) => <span className="pill">{codeWords(r.classification)}</span>,
    },
    {
      key: 'kind',
      header: 'About a person',
      sortValue: (r) => (r.sensitive ? 2 : r.personal ? 1 : 0),
      render: (r) =>
        r.sensitive ? (
          <span className="pill pill-pii">Yes, and sensitive</span>
        ) : r.personal ? (
          <span className="pill pill-pii">Yes</span>
        ) : (
          <span className="muted">No</span>
        ),
    },
  ]

  const subColumns: Column<ExposedSubprocessor>[] = [
    {
      key: 'name',
      header: 'Sub-processor',
      sortValue: (r) => r.legalName,
      render: (r) => (
        <>
          <span className="cell-primary">{r.legalName}</span>
          {r.serviceDescription && <span className="cell-sub">{r.serviceDescription}</span>}
        </>
      ),
    },
    {
      key: 'country',
      header: 'Country',
      sortValue: (r) => r.country ?? null,
      render: (r) =>
        r.country ? <span className="mono">{r.country}</span> : <span className="muted">not stated</span>,
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (r) => r.status,
      render: (r) =>
        r.status === 'WITHDRAWN' ? (
          <span
            className="pill pill-off"
            title="No longer in the chain. Kept because a historic incident is traced through it."
          >
            withdrawn
          </span>
        ) : (
          <span className="pill">{r.status.replace(/_/g, ' ').toLowerCase()}</span>
        ),
    },
    {
      key: 'shared',
      header: 'Also behind',
      numeric: true,
      sortValue: (r) => r.alsoBehindOtherVendors,
      render: (r) =>
        r.alsoBehindOtherVendors > 0 ? (
          <strong>
            {r.alsoBehindOtherVendors} other vendor{r.alsoBehindOtherVendors === 1 ? '' : 's'}
          </strong>
        ) : (
          <span className="muted">no other vendor</span>
        ),
    },
  ]

  const concentrationColumns: Column<ConcentrationEntry>[] = [
    {
      key: 'party',
      header: 'Behind this party',
      sortValue: (r) => r.subprocessorLegalName,
      render: (r) => <span className="cell-primary">{r.subprocessorLegalName}</span>,
    },
    {
      key: 'vendor',
      header: 'This vendor stops too',
      sortValue: (r) => r.vendorLegalName,
      render: (r) => (
        <>
          <a className="cell-primary" href={'#/vendor/' + encodeURIComponent(r.vendorId)}>
            {r.vendorLegalName}
          </a>
          <span className="cell-sub mono">{r.vendorCode}</span>
        </>
      ),
    },
    {
      key: 'tier',
      header: 'Tier',
      sortValue: (r) => r.tier ?? null,
      render: (r) =>
        r.tier ? <>{r.tierLabel || tierLabel(r.tier)}</> : <span className="muted">not classified</span>,
    },
    {
      key: 'band',
      header: 'Band',
      sortValue: (r) => r.band ?? null,
      render: (r) => <BandChip band={r.band} />,
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (r) => r.vendorStatus,
      render: (r) => <span className="pill">{codeWords(r.vendorStatus)}</span>,
    },
  ]

  // The vendor record folds the questions; an incident, where duties are
  // recorded as told on their cards, never does.
  const fold = folded && !onTell

  // The duties as one table (folded): the same words as the cards, a row each.
  const clockColumns: Column<ExposureClock>[] = [
    {
      key: 'duty',
      header: 'Duty',
      render: (c) => {
        const facts = c.because ?? []
        return (
          <>
            <span className="cell-primary vd-inline">
              {c.name}
              <ReasonTip label={'Why ' + c.name + ' is in the catalogue'} text={c.why} />
              <Toggletip label={'Under which instrument: ' + c.name}>{c.instrument + ' ' + c.trigger}</Toggletip>
            </span>
            {c.applicable && facts.length > 0 && (
              <span className="cell-sub vd-inline">
                {'On because ' + facts.map((f) => f.fact + ': ' + f.value).join('; ') + '.'}
                <SourceChip source={facts[0]?.source} />
              </span>
            )}
          </>
        )
      },
    },
    {
      key: 'to',
      header: 'Told to',
      render: (c) => (
        <span className="vd-inline">
          {midSentence(c.owedTo)}
          <Toggletip label={'Who owes ' + c.name}>{'Owed by ' + midSentence(c.owedBy) + '.'}</Toggletip>
        </span>
      ),
    },
    {
      key: 'when',
      header: 'When',
      render: (c) =>
        clockLines(c, exposure.discoveredAt, now).map((l) => (
          <span key={l.key} className="vd-inline">
            {l.line}
          </span>
        )),
    },
    {
      key: 'state',
      header: 'State',
      render: (c) => {
        const st = clockState(c, now)
        return <Status tone={st.tone}>{st.words}</Status>
      },
    },
  ]

  const findingColumns: Column<Exposure['openFindings'][number]>[] = [
    {
      key: 'title',
      header: 'Finding',
      sortValue: (f) => f.title,
      render: (f) => (
        <span className="cell-primary vd-inline">
          {f.title}
          {f.detail && <Toggletip label={'What to do about ' + f.title}>{f.detail}</Toggletip>}
        </span>
      ),
    },
    {
      key: 'severity',
      header: 'Severity',
      render: (f) => <span className="pill">{f.severity}</span>,
    },
    {
      key: 'due',
      header: 'Due',
      sortValue: (f) => f.dueOn,
      render: (f) => <span className="mono">{shortDate(f.dueOn)}</span>,
    },
  ]

  const head = (
    <section className="panel panel-flush" aria-labelledby="exp-head" aria-describedby="exp-basis">
      <div className="panel-head">
        <div>
          <h2 id="exp-head" style={{ margin: 0 }}>
            {exposure.hypothetical ? 'Exposure, if this were found now' : 'Exposure'}
          </h2>
          <p className="muted" style={{ margin: '2px 0 0', fontSize: 12 }}>
            {exposure.vendorLegalName} <span className="mono">{exposure.vendorCode}</span>
            {exposure.tier ? ', ' + (exposure.tierLabel || tierLabel(exposure.tier)) : ', no tier recorded'},
            discovered {stampWithZone(exposure.discoveredAt)}
          </p>
        </div>
        <BandChip band={exposure.band} />
      </div>

      {/* The four answers on one line, in the order they are asked. Each is
          expanded in its own section below. */}
      <div className="stat-strip">
        <div className="stat">
          <b>{data ? codeWords(data.highestClassification) : 'not recorded'}</b>
          <span>highest classification exposed</span>
        </div>
        <div className="stat">
          <b>{data ? data.subjectCountBandWords : 'not recorded'}</b>
          <span>people whose data this vendor handles, as a band</span>
        </div>
        {owedNowAll.length > 0 && (
          <div className="stat alert">
            <b>
              {owedNowAll.length} owed without delay
            </b>
            <span>{owedNowAll.map((c) => c.name).join('; ')}</span>
          </div>
        )}
        <div className={overdueCount > 0 ? 'stat alert' : 'stat'}>
          <b>{next ? countdownWords(Date.parse(next.dueAt) - now) : 'no timed duty'}</b>
          <span>{next ? 'nearest deadline: ' + next.name : 'nothing with a window is engaged'}</span>
        </div>
        <div className="stat">
          <b>{otherVendors}</b>
          <span>other vendors behind the same party</span>
        </div>
      </div>

      <p className="vd-inline vd-quiet" id="exp-basis" style={{ margin: 12 }}>
        What this answer rests on
        <ReasonTip label="What this answer rests on" text={exposure.basis} />
      </p>

      {exposure.hypothetical && (
        <p className="note warn vd-inline" style={{ margin: 12 }} role="status">
          <Status tone="warn">Not recorded</Status>
          A preview against the time you chose
          <Toggletip label="About this preview">
            Nothing was recorded by asking this. Open an incident when there is one, so the clocks count from a time somebody has committed to.
          </Toggletip>
        </p>
      )}

      {onRecompute && (
        <div className="profile-bar" style={{ margin: 12 }}>
          <button type="button" onClick={onRecompute} disabled={busy}>
            {busy ? 'Recomputing' : 'Recompute this answer'}
          </button>
          <span className="hint vd-inline">
            Read at {stampWithZone(exposure.computedAt)}
            <Toggletip label="About this reading">
              {'Computed from ' +
                exposure.rowsRead +
                (exposure.rowsRead === 1 ? ' row' : ' rows') +
                ' in ' +
                exposure.queries +
                (exposure.queries === 1 ? ' query' : ' queries') +
                '. It changes the moment the register does.'}
            </Toggletip>
          </span>
        </div>
      )}
    </section>
  )

  // 1. What is exposed.
  const exposedBody = data ? (
    <>
      <DataTable
        caption={'The data elements declared on the processing record for ' + exposure.vendorLegalName + '.'}
        columns={elementColumns}
        rows={data.elements}
        rowKey={(r) => r.elementCode}
        firstRows={fold ? 10 : undefined}
        empty={{
          title: 'The processing record names no data elements.',
          detail: 'Name the elements under Data on the vendor record.',
        }}
      />
      <dl className="metrics" style={{ margin: 12 }}>
        <div className="metric">
          <dt>Highest classification</dt>
          <dd>{codeWords(data.highestClassification)}</dd>
        </div>
        <div className="metric">
          <dt>Personal data</dt>
          <dd>{data.anyPersonalData ? 'Yes' : 'No'}</dd>
        </div>
        <div className="metric">
          <dt>Sensitive personal</dt>
          <dd>{data.anySensitivePersonalData ? 'Yes' : 'No'}</dd>
        </div>
        <div className="metric">
          <dt>Stored in</dt>
          <dd>{placement?.storageCountry || 'not stated'}</dd>
        </div>
        <div className="metric">
          <dt>Processed in</dt>
          <dd>{placement?.processingCountry || 'not stated'}</dd>
        </div>
      </dl>

      <dl className="kv" style={{ margin: 12 }}>
        <dt>Purpose</dt>
        <dd>{data.processingPurpose || <span className="muted">not recorded</span>}</dd>
        <dt>Retention</dt>
        <dd>
          {data.retentionPeriodMonths ? (
            <>
              {data.retentionPeriodMonths} month{data.retentionPeriodMonths === 1 ? '' : 's'}
              {data.retentionBasis ? ', ' + data.retentionBasis : ''}
            </>
          ) : (
            <span className="muted">not recorded</span>
          )}
        </dd>
        <dt>Deletion</dt>
        <dd>{data.deletionMechanism || <span className="muted">not recorded</span>}</dd>
        <dt>Transfers declared to</dt>
        <dd>
          {placement && placement.transferCountries.length > 0 ? (
            <span className="mono">{placement.transferCountries.join(', ')}</span>
          ) : (
            <span className="muted">none declared</span>
          )}
        </dd>
        <dt>Recorded by</dt>
        <dd>
          {data.recordedBy ? (
            <>
              <span className="mono">{data.recordedBy}</span>
              {data.recordedAt ? ' on ' + shortDate(data.recordedAt) : ''}
            </>
          ) : (
            <span className="muted">not stated</span>
          )}
        </dd>
      </dl>

      {placement?.storedOrProcessedOutsideIndia || placement?.transferredOutsideIndia ? (
        <p className="note vd-inline" id="exp-data-note" style={{ margin: 12 }}>
          <Status tone="warn">Leaves India</Status>
          Settle the transfer position first
          <Toggletip label="About data outside India">
            This record puts data outside India, so the transfer position has to be settled before the particulars go to the Board. The clocks below carry it.
          </Toggletip>
        </p>
      ) : (
        <p className="vd-inline vd-quiet" id="exp-data-note" style={{ margin: 12 }}>
          Recorded by this organisation, not observed at the vendor
        </p>
      )}
    </>
  ) : (
    <div className="empty" id="exp-data-note">
      <strong>Not recorded yet</strong>
      <span className="vd-inline">
        This answer cannot say what is exposed
        <Toggletip label="About a missing record">
          That is an absence of a record, not a finding of no data. Until the data processing record is filled in, the duties below were decided without it.
        </Toggletip>
      </span>
    </div>
  )

  // 2. How many people.
  const peopleBody = data ? (
    <div className="stat-strip" style={{ marginBottom: 10 }}>
      <div className="stat">
        <b>{data.subjectCountBandWords}</b>
        <span>
          band <span className="mono">{data.subjectCountBand}</span>
        </span>
      </div>
      <div className="stat">
        <b>{data.anyPersonalData ? 'Yes' : 'No'}</b>
        <span>data principals involved at all</span>
      </div>
    </div>
  ) : (
    <div className="empty" id="exp-people-note">
      <strong>No band recorded</strong>
      <span className="vd-inline">
        So there is no answer to give
        <Toggletip label="About the subject count band">
          The band sits on the vendor's data processing record. Without it nobody can say whether this is eleven people or eleven lakh.
        </Toggletip>
      </span>
    </div>
  )

  // 3. Who has to be told, and by when.
  const clocksSummary = (
    <>
      <strong>{live.length}</strong> {live.length === 1 ? 'duty' : 'duties'} engaged
      {overdueCount > 0 ? ', ' : '. '}
      {overdueCount > 0 && (
        <>
          <strong>{overdueCount}</strong> past the deadline.{' '}
        </>
      )}
      {toldCount > 0 && (
        <>
          <strong>{toldCount}</strong> told.{' '}
        </>
      )}
      <strong>{considered.length}</strong> considered and not engaged.
    </>
  )

  // 4. Who else sits behind the same supplier.
  const chainSummary = (
    <>
      <strong>{exposure.subprocessors.length}</strong> named behind this vendor,{' '}
      <strong>{otherVendors}</strong> other vendor{otherVendors === 1 ? '' : 's'} reached
    </>
  )
  const chainBody = (
    <>
      <DataTable
        caption={'Sub-processors declared behind ' + exposure.vendorLegalName + '.'}
        columns={subColumns}
        rows={exposure.subprocessors}
        rowKey={(r) => r.id}
        firstRows={fold ? 10 : undefined}
        empty={{
          title: 'No sub-processor has been named behind this vendor.',
          detail: 'Nobody has asked yet, which is not the same as none. Name them under Data.',
        }}
      />

      {exposure.concentration.length > 0 && (
        <>
          <div className="section-head">Other vendors that stop if the same party does</div>
          <DataTable
            caption="Other vendors of this tenant that named one of the same sub-processors."
            columns={concentrationColumns}
            rows={exposure.concentration}
            rowKey={(r) => r.vendorId + ':' + r.subprocessorLegalName}
            firstRows={fold ? 10 : undefined}
            empty={{ title: 'No other vendor names any of these parties.', detail: '' }}
          />
        </>
      )}
    </>
  )

  // Everything below here is real, and none of it is the first call.
  const depBody = (
    <dl className="kv">
      <dt>Business function</dt>
      <dd>{exposure.dependency.businessFunction || <span className="muted">not recorded</span>}</dd>
      <dt>Business process</dt>
      <dd>{exposure.dependency.businessProcess || <span className="muted">not recorded</span>}</dd>
      <dt>Service</dt>
      <dd>{exposure.dependency.serviceDescription || <span className="muted">not recorded</span>}</dd>
      <dt>Criticality</dt>
      <dd>
        {exposure.dependency.serviceCriticality || <span className="muted">not recorded</span>}
        {exposure.dependency.criticalBusinessService ? ', a critical business service' : ''}
      </dd>
      <dt>Dependency level</dt>
      <dd>{exposure.dependency.dependencyLevel || <span className="muted">not recorded</span>}</dd>
      <dt>Reaches customers</dt>
      <dd>{exposure.dependency.customerFacing ? 'Yes' : 'No'}</dd>
      <dt>Holds production access</dt>
      <dd>{exposure.dependency.productionAccess ? 'Yes' : 'No'}</dd>
      <dt>What they do for us</dt>
      <dd>
        {exposure.dependency.engagements.length === 0 ? (
          <span className="muted">no engagement recorded</span>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {exposure.dependency.engagements.map((e, i) => (
              <li key={(e.serviceSummary || 'engagement') + i}>
                {e.serviceSummary || 'engagement recorded without a description'}
                {e.active ? '' : ' (ended)'}
              </li>
            ))}
          </ul>
        )}
      </dd>
    </dl>
  )

  const findSummary = (
    <>
      <strong>{exposure.openFindings.length}</strong> standing finding
      {exposure.openFindings.length === 1 ? '' : 's'}
    </>
  )
  const noFindings = (
    <div className="empty">
      <strong>Nothing was standing at discovery</strong>
      <span className="vd-inline">
        Resolved findings and those with the risk accepted are not counted
        <Toggletip label="Where those are">Both are under Risk on the vendor record.</Toggletip>
      </span>
    </div>
  )

  const caveatsBody = (
    <>
      {exposure.caveats.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>
          Every fact this answer needed was on the record.
        </p>
      ) : (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {exposure.caveats.map((c) => (
            <li key={c} style={{ marginBottom: 6 }}>
              {c}
            </li>
          ))}
        </ul>
      )}
      <p className="vd-inline vd-quiet" id="exp-rests-note" style={{ marginTop: 12 }}>
        Computed when asked, never stored
        <Toggletip label="Why nothing is stored">
          {'Computed at ' +
            stampWithZone(exposure.computedAt) +
            ' and again the next time somebody asks. A stored answer would start disagreeing with the record the day after it was written.'}
        </Toggletip>
      </p>
    </>
  )

  if (fold) {
    // The head answers the four questions on one line; each fold below is
    // one of them, in the order they are asked, saying what it holds while
    // closed. Nothing is left out: every section's content is one press away.
    return (
      <>
        {head}
        <div className="exp-folds">
          <Disclosure
            title="What of ours is exposed"
            summary={
              data
                ? data.elements.length + (data.elements.length === 1 ? ' data element, ' : ' data elements, ') + codeWords(data.highestClassification).toLowerCase()
                : 'not recorded yet'
            }
          >
            {data && (
              <p className="sw-row" style={{ margin: '8px 0' }}>
                <SourceChip source={data.source} />
              </p>
            )}
            {exposedBody}
          </Disclosure>
          <Disclosure title="How many people, approximately" summary={data ? data.subjectCountBandWords : 'no band recorded'}>
            {peopleBody}
          </Disclosure>
          <Disclosure title="Who has to be told, and by when" summary={clocksSummary}>
            <DataTable
              caption={'The duties this answer engages, and the ones it considered, for ' + exposure.vendorLegalName + '.'}
              columns={clockColumns}
              rows={clocks}
              rowKey={(c) => c.code}
              firstRows={10}
              empty={{
                title: 'The catalogue holds no duty.',
                detail: 'A duty missing from this list is missing from the catalogue.',
              }}
            />
          </Disclosure>
          <Disclosure title="Who else is behind the same party" summary={chainSummary}>
            {chainBody}
          </Disclosure>
          <Disclosure
            title="What stops while this vendor is down"
            summary={exposure.dependency.serviceCriticality || exposure.dependency.businessFunction || 'not recorded'}
          >
            {depBody}
          </Disclosure>
          <Disclosure title="What was already known to be wrong here" summary={findSummary}>
            {exposure.openFindings.length === 0 ? (
              noFindings
            ) : (
              <DataTable
                caption={'Findings standing against ' + exposure.vendorLegalName + ' at discovery.'}
                columns={findingColumns}
                rows={exposure.openFindings}
                rowKey={(f) => f.id}
                firstRows={10}
                empty={{ title: 'Nothing was standing at discovery.', detail: '' }}
              />
            )}
          </Disclosure>
          <Disclosure
            title="Not on the record"
            summary={exposure.caveats.length === 0 ? 'nothing missing' : exposure.caveats.length + ' missing'}
          >
            {caveatsBody}
          </Disclosure>
        </div>
      </>
    )
  }

  return (
    <>
      {head}

      {/* 1. What is exposed. */}
      <section className="panel panel-flush" aria-labelledby="exp-data-head" aria-describedby="exp-data-note">
        <div className="panel-head">
          <h3 id="exp-data-head" style={{ margin: 0 }}>
            What of ours is exposed
          </h3>
          {data && <SourceChip source={data.source} />}
        </div>
        {exposedBody}
      </section>

      {/* 2. How many people. */}
      <section className="panel" aria-labelledby="exp-people-head" aria-describedby="exp-people-note">
        <h3 id="exp-people-head" style={{ marginTop: 0 }}>
          How many people, approximately
        </h3>
        {peopleBody}
      </section>

      {/* 3. Who has to be told, and by when. */}
      <section className="panel panel-flush" aria-labelledby="exp-clocks-head" aria-describedby="exp-clocks-note">
        <div className="panel-head">
          <h3 id="exp-clocks-head" style={{ margin: 0 }}>
            Who has to be told, and by when
          </h3>
          <p className="summary-line" style={{ margin: 0 }} role="status">
            {clocksSummary}
          </p>
        </div>

        <div className="act-body">
          {live.length === 0 ? (
            <div className="empty">
              <strong>No duty is engaged by these facts</strong>
              <span className="vd-inline">
                Every clock is listed below with its reason
                <Toggletip label="About no duties">
                  That is the catalogue's answer, not silence. A duty missing from the list below is missing from the catalogue.
                </Toggletip>
              </span>
            </div>
          ) : (
            <ul className="act-list">
              {live.map((c) => (
                <ClockCard key={c.code} clock={c} discoveredAt={exposure.discoveredAt} now={now} onTell={onTell} />
              ))}
            </ul>
          )}

          {considered.length > 0 && (
            <>
              <h4 style={{ margin: '18px 0 0', fontSize: 13 }}>Considered, and not engaged</h4>
              <ul className="act-list">
                {considered.map((c) => (
                  <ClockCard key={c.code} clock={c} discoveredAt={exposure.discoveredAt} now={now} />
                ))}
              </ul>
            </>
          )}

        </div>
      </section>

      {/* 4. Who else sits behind the same supplier. */}
      <section className="panel panel-flush" aria-labelledby="exp-chain-head" aria-describedby="exp-chain-note">
        <div className="panel-head">
          <h3 id="exp-chain-head" style={{ margin: 0 }}>
            Who else is behind the same party
          </h3>
          <p className="summary-line" style={{ margin: 0 }} role="status">
            {chainSummary}
          </p>
        </div>
        {chainBody}
      </section>

      {/* Everything below here is real, and none of it is the first call. */}
      <section className="panel" aria-labelledby="exp-dep-head" aria-describedby="exp-dep-note">
        <h3 id="exp-dep-head" style={{ marginTop: 0 }}>
          What stops while this vendor is down
        </h3>
        {depBody}
      </section>

      <section className="panel panel-flush" aria-labelledby="exp-find-head" aria-describedby="exp-find-note">
        <div className="panel-head">
          <h3 id="exp-find-head" style={{ margin: 0 }}>
            What was already known to be wrong here
          </h3>
          <p className="summary-line" style={{ margin: 0 }}>
            {findSummary}
          </p>
        </div>
        {exposure.openFindings.length === 0 ? (
          noFindings
        ) : (
          <ul className="act-list" style={{ padding: 12 }}>
            {exposure.openFindings.map((f) => (
              <li key={f.id} className="act-item">
                <div className="act-head">
                  <strong>{f.title}</strong>
                  <span className="pill">{f.severity}</span>
                  <span className="act-code mono">due {shortDate(f.dueOn)}</span>
                </div>
                {/* The title, the severity and the date are what somebody
                    scans down. The detail is what to do about this one, and it
                    runs to a paragraph: printed for all of them at once it was
                    twenty-nine paragraphs and nobody read any of them. */}
                {f.detail && (
                  <Why label="What to do about it">
                    <p className="act-detail" style={{ margin: 0 }}>
                      {f.detail}
                    </p>
                  </Why>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="panel" aria-labelledby="exp-rests-head" aria-describedby="exp-rests-note">
        <h3 id="exp-rests-head" style={{ marginTop: 0 }}>
          Not on the record
        </h3>
        {caveatsBody}
      </section>
    </>
  )
}

// A refused answer, said in the words the refusal earned.
//
// A tenant with no active statutory clock version is the one refusal this
// screen must not render as an ordinary error beside four sections of real
// facts: an answer carrying no duties reads as nobody having to be told, and
// that is the most expensive thing here could get wrong.
export function ExposureLoadError({ error }: { error: unknown }): JSX.Element {
  if (noClockCatalogue(error)) {
    return (
      <div className="note stop" role="alert">
        <strong>No statutory clock version is active</strong>
        <span className="vd-inline">
          Publish a clock version, then ask again
          <Toggletip label="Why there is no answer">
            An answer with no duties on it would read as nobody having to be told, so the product refuses to answer until a clock version is active.
          </Toggletip>
        </span>
      </div>
    )
  }
  return <ErrorBox error={error} what="The exposure answer" verb="computed" />
}

// The vendor record's Exposure tab: the same traversal, against a discovery
// time the reader supplies, with no incident recorded.
//
// It exists because the first ten minutes of an incident are spent deciding
// whether there is one. Making somebody open a record before the product will
// tell them what is at stake gets the record opened badly, or not at all.
export function VendorExposure({ vendorId }: { vendorId: string }): JSX.Element {
  const [discovered, setDiscovered] = useState(() => localInputStamp(new Date()))
  // What was actually asked, as against what is in the control. Editing the
  // time must not leave the answer on screen passing for an answer to a
  // question nobody has asked yet.
  const [asked, setAsked] = useState('')
  const [exposure, setExposure] = useState<Exposure | null>(null)
  const [incidents, setIncidents] = useState<Incident[]>([])
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  const compute = useCallback(
    (local: string) => {
      const at = new Date(local)
      if (Number.isNaN(at.getTime())) {
        setError(new Error('Not a time this browser can read. Use the picker.'))
        return
      }
      setBusy(true)
      setError(null)
      api
        .vendorExposure(vendorId, at.toISOString())
        .then((e) => {
          setExposure(e)
          setAsked(local)
        })
        .catch(setError)
        .finally(() => setBusy(false))
    },
    [vendorId],
  )

  useEffect(() => {
    const at = localInputStamp(new Date())
    setDiscovered(at)
    setExposure(null)
    setAsked('')
    compute(at)
    // The whole history for this vendor, not the live list. "Has this happened
    // here before" is what somebody asks the moment a supplier calls a second
    // time, and the ones still running are picked out below.
    api
      .vendorIncidents(vendorId, { limit: 50 })
      .then((page) => setIncidents(page.incidents))
      .catch(() => setIncidents([]))
  }, [vendorId, compute])

  const open = incidents.filter((i) => i.status !== 'CLOSED')

  return (
    <>
      <section className="panel" aria-labelledby="exp-when-head" aria-describedby="exp-when-note">
        <h3 id="exp-when-head" style={{ marginTop: 0 }}>
          Ask before there is a record
        </h3>
        <form
          className="upload"
          onSubmit={(ev) => {
            ev.preventDefault()
            compute(discovered)
          }}
        >
          <div className="field">
            <label htmlFor="exp-discovered">Treat this as discovered at</label>
            <input
              id="exp-discovered"
              type="datetime-local"
              value={discovered}
              aria-describedby="exp-when-note"
              onChange={(ev) => setDiscovered(ev.target.value)}
            />
          </div>
          <button type="submit" className="primary" disabled={busy}>
            {busy ? 'Computing' : 'Compute the answer'}
          </button>
        </form>

        {open.length > 0 && (
          <p className="note warn vd-inline" style={{ marginTop: 10 }} role="status">
            <Status tone="warn">{open.length === 1 ? '1 incident open' : open.length + ' incidents open'}</Status>
            {open.map((i) => (
              <span key={i.id}>
                <a className="mono" href={'#/incident/' + encodeURIComponent(i.id)}>
                  {i.reference}
                </a>
                , discovered {stampWithZone(i.discoveredAt)}
              </span>
            ))}
            <Toggletip label="Work from the record">
              Work from the incident rather than a time typed here, so the clocks everybody quotes are the same clocks.
            </Toggletip>
          </p>
        )}
      </section>

      {error != null && <ExposureLoadError error={error} />}

      {exposure == null && error == null && <Loading what="the exposure answer" />}

      {exposure != null && (
        <>
          {asked !== '' && asked !== discovered && (
            <p className="note warn vd-inline" role="status">
              <Status tone="warn">Time changed</Status>
              Still answering {stampWithZone(new Date(asked).toISOString())}. Compute again to update.
            </p>
          )}
          <ExposureAnswer exposure={exposure} onRecompute={() => compute(discovered)} busy={busy} folded />
        </>
      )}
    </>
  )
}
